import { gray, semantic } from '../design/tokens';
import { PRINT_COLORS } from '../snapshots';
import { FIGURE_WIDTH, figureTitle, footLines, n1, svgFrame, svgLabel, textWidth, wrapText } from './svg';

// A vote matrix as a heatmap: one row per person (an MEP), one column per vote, each cell for / against / abstain /
// absent (or blank when nothing is recorded). Rows may be grouped (a political group) and are drawn group by group.
// Colors are the design tokens' success / danger / warning and a neutral grey; a letter in each cell makes the
// figure readable in black and white. Pure and deterministic.

export const VOTE_VALUES = ['for', 'against', 'abstain', 'absent'] as const;
export type VoteValue = (typeof VOTE_VALUES)[number];
export const isVoteValue = (v: unknown): v is VoteValue => VOTE_VALUES.includes(v as VoteValue);

export const VOTE_LABELS: Record<'en' | 'fr', Record<VoteValue, string>> = {
  en: { for: 'For', against: 'Against', abstain: 'Abstained', absent: 'Absent' },
  fr: { for: 'Pour', against: 'Contre', abstain: 'Abstention', absent: 'Absent' },
};
const LETTER: Record<VoteValue, string> = { for: '+', against: '−', abstain: '○', absent: '·' };

/** The vote relation keys (actor → vote event) and the value each records (the keys are `VOTE_PREDICATES` in review-checks). */
export const VOTE_VALUE_OF: Record<string, VoteValue> = {
  vote_for: 'for',
  vote_against: 'against',
  vote_abstain: 'abstain',
  vote_absent: 'absent',
};

/** A vote matrix read from the records (`POST /reports/votes`; the export draws it again from the same call). */
export interface VoteMatrixData {
  rows: VoteRow[];
  columns: VoteColumn[];
  cells: (VoteValue | null)[][];
  /** Vote claims counted. */
  votes: number;
  /** More persons, votes or claims than the figure shows. */
  truncated: boolean;
}

export const VOTES_MAX_ROWS = 60;
export const VOTES_MAX_COLUMNS = 12;

export interface VoteRow {
  id: string;
  label: string;
  /** Group heading the row sits under ("EPP"), or none. */
  group?: string | null;
}

export interface VoteColumn {
  id: string;
  label: string;
  date?: string | null;
}

export interface VoteMatrixSvgInput {
  title: string;
  rows: readonly VoteRow[];
  columns: readonly VoteColumn[];
  /** `cells[row][column]`, null for nothing recorded. */
  cells: readonly (readonly (VoteValue | null)[])[];
  language?: 'en' | 'fr';
  source?: string | null;
}

const PAD = 20;
const TOP = 44;
const ROW_H = 18;
const HEAD_H = 92;
const GROUP_H = 20;
const LABEL_W = 170;

const CELL_FILL: Record<VoteValue, string> = {
  for: semantic.success.light,
  against: semantic.danger.light,
  abstain: semantic.warning.light,
  absent: gray[300],
};
const CELL_TEXT: Record<VoteValue, string> = {
  for: PRINT_COLORS.background,
  against: PRINT_COLORS.background,
  abstain: PRINT_COLORS.foreground,
  absent: PRINT_COLORS.foreground,
};

/** Rows in the order given, grouped: rows of one group together, groups in the order of their first row. */
export function groupVoteRows(rows: readonly VoteRow[]): { group: string | null; rows: { row: VoteRow; index: number }[] }[] {
  const out: { group: string | null; rows: { row: VoteRow; index: number }[] }[] = [];
  rows.forEach((row, index) => {
    const g = row.group?.trim() || null;
    let bucket = out.find(b => b.group === g);
    if (!bucket) {
      bucket = { group: g, rows: [] };
      out.push(bucket);
    }
    bucket.rows.push({ row, index });
  });
  return out;
}

export function voteMatrixSvg(input: VoteMatrixSvgInput): { svg: string; width: number; height: number } {
  const width = FIGURE_WIDTH;
  const lang = input.language ?? 'en';
  const labels = VOTE_LABELS[lang];
  const rows = input.rows.slice(0, VOTES_MAX_ROWS);
  const columns = input.columns.slice(0, VOTES_MAX_COLUMNS);
  if (!rows.length || !columns.length) {
    const h = TOP + 40;
    return {
      svg: svgFrame(
        width,
        h,
        input.title,
        figureTitle(input.title) + svgLabel('No votes to draw.', PAD, TOP + 16, { fill: PRINT_COLORS.muted }),
      ),
      width,
      height: h,
    };
  }
  const x0 = PAD + LABEL_W;
  const cellW = Math.min(44, (width - PAD - x0) / columns.length);
  const groups = groupVoteRows(rows);
  const grouped = groups.some(g => g.group);
  const parts: string[] = [figureTitle(input.title)];
  // Column headings, slanted so twelve fit.
  columns.forEach((c, k) => {
    const cx = x0 + k * cellW + cellW / 2;
    const text = c.date ? `${c.label} (${c.date})` : c.label;
    parts.push(
      `<text transform="translate(${n1(cx)} ${TOP + HEAD_H - 8}) rotate(-60)" font-size="9.5" fill="${PRINT_COLORS.foreground}" text-anchor="start" dominant-baseline="middle"><title>${text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')}</title>${
        text.length > 22
          ? `${text.slice(0, 21).replace(/&/g, '&amp;').replace(/</g, '&lt;')}…`
          : text.replace(/&/g, '&amp;').replace(/</g, '&lt;')
      }</text>`,
    );
  });
  let y = TOP + HEAD_H;
  for (const g of groups) {
    if (grouped) {
      parts.push(
        svgLabel(g.group ?? (lang === 'fr' ? 'Sans groupe' : 'No group'), PAD, y + GROUP_H / 2, {
          size: 10,
          weight: 600,
          fill: PRINT_COLORS.muted,
          max: 30,
        }),
      );
      y += GROUP_H;
    }
    for (const { row, index } of g.rows) {
      parts.push(svgLabel(row.label, x0 - 8, y + ROW_H / 2, { size: 10, max: 26, align: 'left' }));
      columns.forEach((_, k) => {
        const v = input.cells[index]?.[k] ?? null;
        const x = x0 + k * cellW;
        parts.push(
          `<rect x="${n1(x + 1)}" y="${n1(y + 1)}" width="${n1(cellW - 2)}" height="${ROW_H - 2}" rx="2" fill="${v ? CELL_FILL[v] : PRINT_COLORS.background}" stroke="${PRINT_COLORS.grid}" stroke-width="1"${v ? ` data-vote="${v}"` : ''}/>`,
          v
            ? svgLabel(LETTER[v], x + cellW / 2, y + ROW_H / 2, { size: 10, weight: 700, fill: CELL_TEXT[v], align: 'center', max: 2 })
            : '',
        );
      });
      y += ROW_H;
    }
  }
  // Legend.
  const ly = y + 16;
  let lx = PAD;
  for (const v of VOTE_VALUES) {
    parts.push(
      `<rect x="${lx}" y="${ly - 6}" width="12" height="12" rx="2" fill="${CELL_FILL[v]}"/>`,
      svgLabel(`${LETTER[v]} ${labels[v]}`, lx + 16, ly, { size: 10, fill: PRINT_COLORS.muted, max: 20 }),
    );
    lx += 16 + textWidth(`${LETTER[v]} ${labels[v]}`, 10) + 18;
  }
  const foot = input.source ? footLines(wrapText(input.source, 135, 2), ly + 22) : null;
  const height = Math.round(ly + 14 + (foot ? foot.height + 8 : 0));
  if (foot) parts.push(foot.svg);
  const counts = VOTE_VALUES.map(v => `${labels[v]} ${input.cells.flat().filter(x => x === v).length}`).join(', ');
  return {
    svg: svgFrame(width, height, `${input.title}. ${rows.length} rows, ${columns.length} votes. ${counts}.`, parts.join('\n')),
    width,
    height,
  };
}

/** The matrix as a table: one row per person, one column per vote, cells in words. */
export function voteMatrixTable(
  input: Pick<VoteMatrixSvgInput, 'rows' | 'columns' | 'cells' | 'language'>,
  first: string,
): { headers: string[]; rows: string[][] } {
  const labels = VOTE_LABELS[input.language ?? 'en'];
  const columns = input.columns.slice(0, VOTES_MAX_COLUMNS);
  return {
    headers: [first, ...columns.map(c => (c.date ? `${c.label} (${c.date})` : c.label))],
    rows: input.rows.slice(0, VOTES_MAX_ROWS).map((r, i) => [
      r.label,
      ...columns.map((_, k) => {
        const v = input.cells[i]?.[k] ?? null;
        return v ? labels[v] : '';
      }),
    ]),
  };
}
